import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

export interface FeatureFlagVariation {
  name: string;
  data: Record<string, unknown>;
}

export interface FeatureFlagsProvider {
  /**
   * @returns FeatureFlagVariation if the flag is enabled
   * @returns null if the flag is explicitly disabled
   * @returns undefined if the provider does not know this flag
   */
  getFeatureFlagVariation(name: string): FeatureFlagVariation | null | undefined;
}

type LocalFlagValue = boolean | 'providers' | FeatureFlagVariation;

/**
 * Central service for managing feature flags.
 *
 * Feature flags can be configured in `environment.featureFlags` with the following values:
 * - `true`: Feature enabled for all users (returns default variation)
 * - `false`: Feature disabled for all users
 * - `'providers'`: Delegates decision to registered external providers (e.g., Kameleoon)
 * - `{ name, data }`: Feature enabled with a specific local variation
 *
 * @example
 * // Check if a feature is active
 * if (featureFlagService.isFeatureFlagActive('my_feature')) {
 *   // Feature is enabled
 * }
 *
 * @example
 * // Get variation data for A/B testing
 * const variation = featureFlagService.getFeatureFlagVariation('ab_test');
 * if (variation) {
 *   console.log(variation.name);  // e.g., 'variant_a'
 *   console.log(variation.data);  // e.g., { buttonColor: 'blue' }
 * }
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagsService {
  private providers: FeatureFlagsProvider[] = [];

  /**
   * Registers an external feature flag provider.
   * Providers are queried in registration order when a flag is set to `'providers'`.
   * The first provider to return a defined value (not `undefined`) wins.
   *
   * @param provider - The provider implementing `FeatureFlagProvider` interface
   */
  registerProvider(provider: FeatureFlagsProvider): void {
    this.providers.push(provider);
  }

  /**
   * Checks if a feature flag is active.
   *
   * @param name - The feature flag identifier
   * @returns `true` if the feature is enabled, `false` otherwise
   */
  isFeatureFlagActive(name: string): boolean {
    return this.getFeatureFlagVariation(name) !== null;
  }

  /**
   * Gets the variation for a feature flag.
   *
   * @param name - The feature flag identifier
   * @returns The variation object if enabled, `null` if disabled or not found
   *
   * @example
   * // Flag set to `true` returns default variation
   * { name: 'default', data: {} }
   *
   * @example
   * // Flag set to a variation object returns it as-is
   * { name: 'variant_a', data: { color: 'blue' } }
   */
  getFeatureFlagVariation(name: string): FeatureFlagVariation | null {
    const localValue = this.getLocalFlagValue(name);

    // Flag not defined or disabled
    if (localValue === undefined || localValue === false) {
      return null;
    }

    // Flag enabled without specific variation
    if (localValue === true) {
      return { name: 'default', data: {} };
    }

    // Delegate to external providers
    if (localValue === 'providers') {
      return this.getVariationFromProviders(name);
    }

    // Locally defined variation
    return localValue;
  }

  private getLocalFlagValue(name: string): LocalFlagValue | undefined {
    // Cast to support environments without featureFlags
    const env = environment as { featureFlags?: Record<string, LocalFlagValue> };
    const flags = env.featureFlags ?? {};
    return flags[name];
  }

  private getVariationFromProviders(name: string): FeatureFlagVariation | null {
    for (const provider of this.providers) {
      const variation = provider.getFeatureFlagVariation(name);
      // undefined = the provider does not know this flag (skip to next provider)
      // null = the flag is explicitly disabled (return immediately)
      // FeatureFlagVariation = the flag is enabled (return immediately)
      if (variation !== undefined) {
        return variation;
      }
    }

    // No provider knows this flag
    return null;
  }
}
